import React from 'react'
import classNames from 'classnames'
import {Avatar, message} from 'antd'
import {Link} from 'react-router-dom'
import copyToClipboard from 'copy-to-clipboard'
import {FaLinkSlash, FaUserGraduate} from 'react-icons/fa6'
import {HiPhone} from 'react-icons/hi2'
import {MdOutlineEmail} from 'react-icons/md'
import {IoCopyOutline} from 'react-icons/io5'
import {PiBookOpenTextFill, PiLinkSimpleBold} from 'react-icons/pi'
import {BsChevronRight, BsEyeFill, BsPeopleFill} from 'react-icons/bs'
import getAliasName from '../../../../helpers/getAliasName'
import {formatDate} from '../../../../helpers/datetimeHelpers'
import getLinkToDetail from '../../../../helpers/getLinkToDetail'
import {getGroupColor} from './renderCourseClassStudent'
import {getInitials, renderGenderBadge} from './renderStudent'
import {GoDotFill} from "react-icons/go";

// "Phụ huynh" = 1 User có liên kết (student_user_links, relationship != SELF, status=ACTIVE) tới
// >=1 học viên - xem StudentLinkController::getParents + ParentGroupTransformer (backend). Mỗi
// "item" ở đây là 1 NHÓM đã gộp theo user_id (không phải 1 student_user_link đơn lẻ), kèm mảng
// `children` (StudentTransformerBasic, cùng field với Students.js) - KHÔNG dùng father_name/
// mother_name/father_phone_number cũ trên students (dữ liệu legacy, có thể trống dù đã liên kết
// qua hệ thống mới - đúng lý do redesign trang này).
const RELATIONSHIP_STYLE = {
    FATHER: {label: 'father', className: 'bg-blue-50 text-blue-600'},
    MOTHER: {label: 'mother', className: 'bg-pink-50 text-pink-600'},
    BROTHER: {label: 'brother / sister', className: 'bg-indigo-50 text-indigo-600'},
    SISTER: {label: 'sister', className: 'bg-indigo-50 text-indigo-600'},
    GUARDIAN: {label: 'guardian', className: 'bg-amber-50 text-amber-500'},
}

const renderRelationshipBadge = (relationship, t) => {
    const style = RELATIONSHIP_STYLE[relationship]
    if (!style) return null
    return (
        <span className={classNames("px-2 py-0.5 rounded-full text-2xs font-bold w-fit whitespace-no-wrap", style.className)}>
            {t(style.label)}
        </span>
    )
}

// Trạng thái "đã liên kết tài khoản" ở đây lấy từ `child.user` (field legacy students.user_id,
// StudentTransformerBasic) - CHƯA cộng thêm student_user_links relationship=SELF của chính con
// (khác cách đã sửa cho renderStudentCardItem, vì endpoint phụ huynh không load kèm dữ liệu đó) -
// chỉ mang tính tham khảo nhanh, xem chính xác nhất ở "Chi tiết" của học viên đó.
const ChildRow = ({child, t}) => {
    const {
        avatar, user, fullname, student_idcode,
        gender, date_of_birth, course_class,
    } = child ?? {}
    const patronSaint = getAliasName(child, 'patron_saint')
    const isChildLinked = !!user?.id
    const isUnknownDob = !date_of_birth

    return (
        <div className="py-2 flex items-start gap-2">
            <Avatar
                size={28}
                src={avatar || user?.avatar}
                icon={<FaUserGraduate size={13} className="text-color-500"/>}
                className="flex-shrink-0 flex flex-center background-200"
            />
            <div className="flex flex-col min-w-0 flex-1 gap-0.5">
                <div className="flex items-baseline gap-1.5 flex-wrap">
                    {patronSaint && (
                        <span className="font-medium text-2xs text-gray-600">{patronSaint}</span>
                    )}
                    <span className="font-semibold text-xs text-color-000 truncate">{fullname}</span>
                    {student_idcode && (
                        <span
                            onClick={() => { copyToClipboard(student_idcode); message.info(t('copied')) }}
                            className="font-mono text-2xs text-blue-500 hover:text-blue-700 cursor-pointer flex items-center gap-0.5 whitespace-no-wrap">
                            #{student_idcode}
                            <IoCopyOutline size={8}/>
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-1.5 flex-wrap text-2xs text-color-300">
                    {renderGenderBadge(gender, t)}
                    {(gender === 1 || gender === 2) && date_of_birth && <GoDotFill size={6} />}
                    {date_of_birth && <span className="font-medium">{formatDate(date_of_birth, 'DD/MM/YYYY')}</span>}
                    {isChildLinked ? (
                        <span className="ml-1 flex items-center gap-0.5 font-medium text-green-600">
                            <PiLinkSimpleBold size={10}/>{t('linked account')}
                        </span>
                    ) : (
                        <span className="ml-1 flex items-center gap-0.5 text-color-400 italic">
                            <FaLinkSlash size={9}/>{t('no linked account')}
                        </span>
                    )}
                </div>
                {course_class && (
                    <span className="flex items-center gap-1.5 text-2xs italic text-color-400 truncate">
                        <PiBookOpenTextFill size={11} className="flex-shrink-0"/>
                        <span className="font-medium text-color-100 not-italic">{course_class?.title}</span>
                        {t('belong to course').toLowerCase()}
                        <span className="font-medium text-gray-700 not-italic">{course_class?.course?.title}</span>
                    </span>
                )}
            </div>
        </div>
    )
}

const ParentContactBox = ({contact_phone, contact_email, t}) => (
    <div className="px-3 py-2 rounded-xl background-100 flex flex-col gap-1">
        <span className="flex items-center gap-1.5 text-xs">
            <HiPhone size={12} className="flex-shrink-0 text-color-400"/>
            {contact_phone ? (
                <span className="text-color-200 font-medium">{contact_phone}</span>
            ) : (
                <span className="text-color-300 italic">{t('not update')}</span>
            )}
        </span>
        <span className="flex items-center gap-1.5 text-xs">
            <MdOutlineEmail size={12} className="flex-shrink-0 text-color-400"/>
            {contact_email ? (
                <span className="text-color-200 font-medium truncate">{contact_email}</span>
            ) : (
                <span className="text-color-300 italic">{t('not update')}</span>
            )}
        </span>
    </div>
)

export const renderParentCardItem = ({translate: t} = {}) => (item, index) => {
    const {user, relationship, contact_phone, contact_email, children = [], children_count = 0} = item ?? {}
    const initials = getInitials(user?.name)
    const color = getGroupColor(user?.name)
    const patronSaint = user?.patron_saint_name

    return (
        <div key={index} className="p-4 flex flex-col gap-3 border border-color-100 background rounded-2xl hover:shadow-md transition-all duration-300 custom-item-render">
            {/* Header: avatar + quan hệ + tên + @username */}
            <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2.5 min-w-0">
                    {user?.avatar ? (
                        <Avatar size={40} src={user.avatar} className="flex-shrink-0 flex flex-center background-200"/>
                    ) : (
                        <div
                            style={{width: 40, height: 40, backgroundColor: color, flexShrink: 0}}
                            className="rounded-full flex flex-center">
                            <span className="text-white font-bold text-sm leading-none">{initials}</span>
                        </div>
                    )}
                    <div className="flex flex-col gap-0.5 min-w-0">
                        {patronSaint && (
                            <span className="mr-1 font-medium text-xs text-gray-600">{patronSaint}</span>
                        )}
                        <span className="font-bold text-sm text-color-000 leading-snug truncate">
                            {user?.name}
                        </span>
                    </div>
                </div>

                {renderRelationshipBadge(relationship, t)}
                {/*{user?.username && (*/}
                {/*    <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 font-mono text-2xs font-semibold flex-shrink-0 whitespace-no-wrap">*/}
                {/*        @{user.username}*/}
                {/*    </span>*/}
                {/*)}*/}
            </div>

            <ParentContactBox contact_phone={contact_phone} contact_email={contact_email} t={t}/>

            {/* Học viên con */}
            <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-1.5 text-2xs font-bold text-gray-500 uppercase tracking-wide">
                        <BsPeopleFill size={12}/>
                        {t('children studying')} ({children_count})
                    </span>
                    {children_count > 1 && (
                        <span className="px-2 py-0.5 rounded-full bg-secondary-50 text-secondary-600 text-2xs font-semibold whitespace-no-wrap">
                            {t('multiple children studying')}
                        </span>
                    )}
                </div>
                <div className="flex flex-col divide-y divide-color-50">
                    {children.map((child, i) => <ChildRow key={i} child={child} t={t}/>)}
                </div>
            </div>

            <div className="flex items-center justify-end pt-2 border-t border-color-50">
                <Link
                    to={getLinkToDetail(user)}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary-50 hover:bg-primary-100 text-primary-600 font-semibold text-xs no-underline whitespace-no-wrap">
                    {t('view parent profile')}
                    <BsChevronRight size={11}/>
                </Link>
            </div>
        </div>
    )
}

export const renderParentTableRow = ({translate: t} = {}) => (item, index) => {
    const {user, relationship, contact_phone, contact_email, children = []} = item ?? {}
    const initials = getInitials(user?.name)
    const color = getGroupColor(user?.name)
    const patronSaint = user?.patron_saint_name

    return (
        <tr key={index} className="border-t border-color-50 background hover:background-50 transition-colors align-top">
            <td className="px-3 py-2.5 text-center text-xs text-color-400 font-medium">{index + 1}</td>
            <td className="px-3 py-2.5">
                <div className="flex items-start gap-2.5 min-w-0">
                    {user?.avatar ? (
                        <Avatar size={32} src={user.avatar} className="flex-shrink-0 flex flex-center background-200"/>
                    ) : (
                        <div
                            style={{width: 32, height: 32, backgroundColor: color, flexShrink: 0}}
                            className="rounded-full flex flex-center">
                            <span className="text-white font-bold text-xs leading-none">{initials}</span>
                        </div>
                    )}
                    <div className="flex flex-col gap-0.5 min-w-0">
                        {patronSaint && (
                            <span className="text-xs font-medium text-gray-600 leading-snug truncate">{patronSaint}</span>
                        )}
                        <span className="font-bold text-sm text-color-000 leading-snug truncate">{user?.name}</span>
                        {renderRelationshipBadge(relationship, t)}
                        {/*{user?.username && (*/}
                        {/*    <span className="w-fit px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 font-mono text-2xs font-semibold whitespace-no-wrap">*/}
                        {/*        @{user.username}*/}
                        {/*    </span>*/}
                        {/*)}*/}
                    </div>
                </div>
            </td>
            <td className="px-3 py-2.5">
                <div className="flex flex-col divide-y divide-color-50">
                    {children.map((child, i) => <ChildRow key={i} child={child} t={t}/>)}
                </div>
            </td>
            <td className="px-3 py-2.5">
                <div className="flex flex-col gap-0.5 text-xs text-color-300">
                    <span className="flex items-center gap-1 whitespace-no-wrap">
                        <HiPhone size={10} className="flex-shrink-0"/>
                        {contact_phone || <span className="text-color-400 italic">{t('not update')}</span>}
                    </span>
                    <span className="flex items-center gap-1 truncate max-w-40">
                        <MdOutlineEmail size={10} className="flex-shrink-0"/>
                        {contact_email || <span className="text-color-400 italic">{t('not update')}</span>}
                    </span>
                </div>
            </td>
            <td className="px-3 py-2.5">
                <div className="flex items-center justify-end">
                    <Link
                        to={getLinkToDetail(user)}
                        style={{width: 28, height: 28}}
                        className="flex flex-center rounded-full border border-color-200 hover:border-primary hover:background-100 text-color-400 hover:text-primary">
                        <BsEyeFill size={13}/>
                    </Link>
                </div>
            </td>
        </tr>
    )
}
